import { IS_ENV_DEVELOPMENT } from "../dataProvider/API";
import { GCP_BUCKET_URL_AVATARS } from "../constants/data";

const LIST_KEYS = [
  "governingBodies",
  "governingBodiesDto",
  "governingBodyDtos",
  "dtos",
  "data",
  "content",
  "items",
];

const STATUS_VALUES = ["ACTIVE", "INACTIVE", "DEPRECATED"];
const LOGO_SUFFIX_REGEX = /_(small|master|original)$/i;

const toSafeString = (value) => (value == null ? "" : String(value));

const toCanonicalObjectKey = (dto) => {
  const explicitKey = toSafeString(dto?.imageObjectKey || dto?.image_object_key).trim();
  const fallbackId = dto?.id ?? null;

  let key = explicitKey;
  if (!key && fallbackId != null) {
    key = `governingBodies/gb_${fallbackId}`;
  }
  if (!key) return "";

  // If backend returns full URL, keep only object path after logo_images/.
  if (/^https?:\/\//i.test(key)) {
    const marker = "/logo_images/";
    const idx = key.indexOf(marker);
    if (idx >= 0) {
      key = key.slice(idx + marker.length);
    }
  }

  key = key.replace(/^\/+/, "");
  key = key.replace(/^logo_images\//i, "");
  key = key.replace(LOGO_SUFFIX_REGEX, "");
  return key;
};

export const resolveGoverningBodyLogoUrl = (dto, variant = "master") => {
  const safeVariant = variant === "small" ? "small" : "master";
  const canonicalKey = toCanonicalObjectKey(dto);
  if (!canonicalKey) return "";
  return `${GCP_BUCKET_URL_AVATARS}${canonicalKey}_${safeVariant}`;
};

const getTranslation = (translations, locale = "en-US") => {
  if (!Array.isArray(translations)) return null;
  return (
    translations.find((item) => item?.locale === locale) ||
    translations.find((item) => item?.locale === "en-US") ||
    translations[0] ||
    null
  );
};

export const formatDateYmd = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

export const extractGoverningBodiesRows = (payload) => {
  if (!payload || typeof payload !== "object") return [];

  for (let i = 0; i < LIST_KEYS.length; i += 1) {
    const key = LIST_KEYS[i];
    if (Array.isArray(payload[key])) {
      return payload[key];
    }
  }

  if (Array.isArray(payload)) return payload;
  return [];
};

export const extractGoverningBodiesTotal = (payload, fallbackRows = []) => {
  if (!payload || typeof payload !== "object") return fallbackRows.length;
  return (
    payload.totalElements ??
    payload.total ??
    payload.totalCount ??
    fallbackRows.length
  );
};

export const extractGoverningBodiesPage = (payload) => {
  if (!payload || typeof payload !== "object") return 0;
  return payload.currentPage ?? payload.page ?? payload.pageNumber ?? 0;
};

export const normalizeGoverningBodyRow = (dto, locale = "en-US") => {
  const translations = Array.isArray(dto?.translations) ? dto.translations : [];
  const translation = getTranslation(translations, locale);

  const name = toSafeString(dto?.name || translation?.name);
  const shortName = toSafeString(dto?.shortName || translation?.shortName);
  const status = STATUS_VALUES.includes(dto?.status) ? dto.status : "INACTIVE";

  const normalized = {
    id: dto?.id ?? null,
    abbreviation: toSafeString(dto?.abbreviation),
    imageObjectKey: toCanonicalObjectKey(dto) || null,
    imageUrl: dto?.imageUrl ?? null,
    imageSmallUrl: resolveGoverningBodyLogoUrl(dto, "small"),
    imageMasterUrl: resolveGoverningBodyLogoUrl(dto, "master"),
    name,
    shortName,
    outputSequence: dto?.outputSequence ?? null,
    ddListId: dto?.ddListId ?? null,
    ddListAbbreviation: toSafeString(
      dto?.ddListAbbreviation ?? dto?.ddListDescription,
    ),
    ddListDescription: toSafeString(dto?.ddListDescription),
    parentId: dto?.parentId ?? null,
    parentName: toSafeString(dto?.parentName),
    parentShortName: toSafeString(dto?.parentShortName),
    teamType: toSafeString(dto?.teamType),
    teamId: dto?.teamId ?? null,
    teamName: toSafeString(dto?.teamName),
    ageCutoffMonth:
      dto?.ageCutoffMonth ??
      dto?.ageCutOffMonth ??
      dto?.age_cutoff_month ??
      null,
    ageCutoffDay:
      dto?.ageCutoffDay ??
      dto?.ageCutOffDay ??
      dto?.age_cutoff_day ??
      null,
    status,
    createdAt: dto?.createdAt ?? null,
    createdBy: dto?.createdBy ?? null,
    updatedAt: dto?.updatedAt ?? null,
    updatedBy: dto?.updatedBy ?? null,
    updatedByName: toSafeString(dto?.updatedByName),
    translations,
    ruleGroups: Array.isArray(dto?.ruleGroups) ? dto.ruleGroups : [],
    pointSchemas: Array.isArray(dto?.pointSchemas) ? dto.pointSchemas : [],
    pointSchemaDefaults: Array.isArray(dto?.pointSchemaDefaults)
      ? dto.pointSchemaDefaults
      : [],
  };

  if (IS_ENV_DEVELOPMENT && !normalized.imageUrl && normalized.imageObjectKey) {
    // eslint-disable-next-line no-console
    console.warn(
      "Governing body row missing imageUrl; imageObjectKey provided only:",
      normalized.imageObjectKey,
    );
  }

  return normalized;
};

export const normalizeGoverningBodiesPayload = (payload, locale = "en-US") => {
  const rows = extractGoverningBodiesRows(payload).map((row) =>
    normalizeGoverningBodyRow(row, locale),
  );
  const total = extractGoverningBodiesTotal(payload, rows);
  const currentPage = extractGoverningBodiesPage(payload);

  return { rows, total, currentPage };
};
